import { useMemo, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import { Download, Search } from "lucide-react";
import {
  DashboardDataTablePanel,
  DashboardDataTablePanelHeader,
  DashboardDataTableScroll,
  DashboardDataTableSortButton,
  DashboardDataTableTable,
  DefinitionTooltip,
  TablePagePagination,
  dashboardDataTable as dt,
  sortRowsByKeyAccessor,
  useDashboardTableSort,
} from "src/components/common";
import type { PlatformReportRow } from "../../data/paidMediaPlatformReportMockData";
import { getOverviewTooltip } from "../../utils/paidMediaOverviewTooltips";
import { downloadCsv } from "../../utils/paidMediaCsvExport";
import { getRoasToneClass } from "../metaAds/metaTableConfig";
import {
  platformReportSectionHelp,
  PLATFORM_REPORT_PAGE_SIZE,
  PLATFORM_REPORT_SORT_GETTERS,
  PLATFORM_REPORT_STATUS_LABEL_KEY,
  type PlatformReportSortKey,
} from "./platformReportConfig";
import { formatPlatformReportValue } from "./platformReportFormat";
import { formatNumber, formatNumberDigits, stripBidiIsolateMarks } from "src/utils/numberFormat";

type PlatformReportMetricKey = Exclude<PlatformReportSortKey, "name">;

/** Numeric columns, in the order the Meta and Google campaign tables use. */
const METRIC_COLUMNS: Array<{
  key: PlatformReportMetricKey;
  labelKey: string;
  tooltipId: string;
  format: Parameters<typeof formatPlatformReportValue>[1];
  center?: boolean;
  useClickLabel?: boolean;
}> = [
  { key: "spend", labelKey: "platformAdsReport.kpis.spend", tooltipId: "colSpend", format: "money" },
  {
    key: "revenue",
    labelKey: "platformAdsReport.kpis.revenue",
    tooltipId: "colRevenue",
    format: "money",
  },
  {
    key: "impressions",
    labelKey: "platformAdsReport.kpis.impressions",
    tooltipId: "colImpressions",
    format: "count",
    center: true,
  },
  {
    key: "clicks",
    labelKey: "platformAdsReport.kpis.clicks",
    tooltipId: "colClicks",
    format: "count",
    center: true,
    useClickLabel: true,
  },
  {
    key: "ctr",
    labelKey: "platformAdsReport.kpis.ctr",
    tooltipId: "colCtr",
    format: "percent",
    center: true,
  },
  {
    key: "cpc",
    labelKey: "platformAdsReport.kpis.cpc",
    tooltipId: "colCpc",
    format: "unitCost",
    center: true,
  },
  {
    key: "purchases",
    labelKey: "platformAdsReport.kpis.purchases",
    tooltipId: "colPurchases",
    format: "count",
    center: true,
  },
  {
    key: "costPerPurchase",
    labelKey: "platformAdsReport.kpis.costPerPurchase",
    tooltipId: "colCpp",
    format: "unitCost",
    center: true,
  },
  {
    key: "roas",
    labelKey: "platformAdsReport.kpis.roas",
    tooltipId: "colRoas",
    format: "multiplier",
    center: true,
  },
];

interface PlatformReportTableProps {
  sectionHelpKey: Parameters<typeof platformReportSectionHelp>[1];
  title: string;
  rows: PlatformReportRow[];
  nameColumnLabel: string;
  descriptorColumnLabel: string;
  clickLabel: string;
  searchPlaceholder: string;
  searchAriaLabel: string;
  csvFilename: string;
}

const PlatformReportTable: FC<PlatformReportTableProps> = ({
  sectionHelpKey,
  title,
  rows,
  nameColumnLabel,
  descriptorColumnLabel,
  clickLabel,
  searchPlaceholder,
  searchAriaLabel,
  csvFilename,
}) => {
  const { t, i18n } = useTranslation();
  const { sort, toggleColumn } = useDashboardTableSort<PlatformReportSortKey>({
    key: "spend",
    direction: "desc",
  });
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  const sortedRows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matched = needle
      ? rows.filter(
          row =>
            row.name.toLowerCase().includes(needle) ||
            row.descriptor.toLowerCase().includes(needle)
        )
      : rows;
    return sortRowsByKeyAccessor(matched, sort, PLATFORM_REPORT_SORT_GETTERS);
  }, [rows, query, sort]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / PLATFORM_REPORT_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedRows = sortedRows.slice(
    (currentPage - 1) * PLATFORM_REPORT_PAGE_SIZE,
    currentPage * PLATFORM_REPORT_PAGE_SIZE
  );

  const columnLabel = (column: (typeof METRIC_COLUMNS)[number]) =>
    column.useClickLabel ? clickLabel : t(column.labelKey);

  const handleSort = (key: PlatformReportSortKey) => {
    toggleColumn(key);
    setPage(1);
  };

  // CSV/Excel does its own RTL layout and renders bidi isolate marks as literal placeholder
  // glyphs, so export cells localize digits but must not carry the wrapper the UI relies on.
  const formatExportCell = (column: (typeof METRIC_COLUMNS)[number], row: PlatformReportRow) => {
    const value = row[column.key];
    if (value === null || value === undefined || !Number.isFinite(value)) {
      return "";
    }
    if (column.format === "percent") {
      return stripBidiIsolateMarks(
        `${formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 }, i18n.language)}%`
      );
    }
    if (column.format === "multiplier") {
      return stripBidiIsolateMarks(
        `${formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 }, i18n.language)}x`
      );
    }
    if (column.format === "money" || column.format === "unitCost") {
      return stripBidiIsolateMarks(formatPlatformReportValue(value, column.format));
    }
    return formatNumberDigits(Math.round(value), {}, i18n.language);
  };

  const handleExport = () => {
    downloadCsv(
      csvFilename,
      [
        nameColumnLabel,
        descriptorColumnLabel,
        t("platformAdsReport.columns.status"),
        ...METRIC_COLUMNS.map(columnLabel),
      ],
      sortedRows.map(row => [
        row.name,
        row.descriptor,
        t(PLATFORM_REPORT_STATUS_LABEL_KEY[row.status] ?? row.status),
        ...METRIC_COLUMNS.map(column => formatExportCell(column, row)),
      ])
    );
  };

  return (
    <DashboardDataTablePanel className="paid-media-table-panel--attached paid-media-campaign-table-panel">
      <DashboardDataTablePanelHeader>
        <div className="paid-media-panel__heading">
          <h3>{title}</h3>
          <DefinitionTooltip label={title} info={platformReportSectionHelp(t, sectionHelpKey)} />
        </div>
        <div className="paid-media-table-toolbar" role="toolbar" aria-label={searchAriaLabel}>
          <label className={`${dt.search} paid-media-table-toolbar__search`}>
            <Search size={16} />
            <input
              type="search"
              value={query}
              placeholder={searchPlaceholder}
              onChange={event => {
                setQuery(event.target.value);
                setPage(1);
              }}
            />
          </label>
          <button
            type="button"
            className="paid-media-export-btn"
            onClick={handleExport}
            disabled={sortedRows.length === 0}
          >
            <Download size={16} aria-hidden />
            {t("metaAdsReport.export.csv")}
          </button>
        </div>
      </DashboardDataTablePanelHeader>

      <DashboardDataTableScroll>
        <DashboardDataTableTable className="paid-media-campaign-performance-table">
          <thead>
            <tr>
              <th>
                <DashboardDataTableSortButton onClick={() => handleSort("name")}>
                  {nameColumnLabel}
                </DashboardDataTableSortButton>
              </th>
              <th className={dt.number}>
                <span className={dt.thHelpRow}>
                  {descriptorColumnLabel}
                  <DefinitionTooltip
                    label={descriptorColumnLabel}
                    info={getOverviewTooltip(t, "colObjective", "platform")}
                    formulaTitle={t("webAnalytics.kpi.formula")}
                  />
                </span>
              </th>
              <th className={dt.thCenter}>
                <span className={dt.thHelpRow}>
                  {t("platformAdsReport.columns.status")}
                  <DefinitionTooltip
                    label={t("platformAdsReport.columns.status")}
                    info={getOverviewTooltip(t, "colStatus", "platform")}
                    formulaTitle={t("webAnalytics.kpi.formula")}
                  />
                </span>
              </th>
              {METRIC_COLUMNS.map((column, columnIndex) => (
                <th key={column.key} className={column.center ? dt.thCenter : dt.number}>
                  <span className={dt.thHelpRow}>
                    <DashboardDataTableSortButton onClick={() => handleSort(column.key)}>
                      {columnLabel(column)}
                    </DashboardDataTableSortButton>
                    <DefinitionTooltip
                      label={columnLabel(column)}
                      info={getOverviewTooltip(t, column.tooltipId, "platform")}
                      align={columnIndex >= METRIC_COLUMNS.length - 3 ? "end" : "start"}
                      formulaTitle={t("webAnalytics.kpi.formula")}
                    />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pagedRows.map(row => (
              <tr key={row.id}>
                <td>
                  <div className="paid-media-campaign-cell">
                    <div>
                      <strong>{row.name}</strong>
                      {row.parentName ? (
                        <span className="paid-media-mini-tag paid-media-mini-tag--muted">
                          {row.parentName}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </td>
                <td className={dt.number}>{row.descriptor}</td>
                <td className={dt.tdCenter}>
                  <span className="paid-media-mini-tag">
                    {t(PLATFORM_REPORT_STATUS_LABEL_KEY[row.status] ?? row.status)}
                  </span>
                </td>
                {METRIC_COLUMNS.map(column => {
                  const value = formatPlatformReportValue(row[column.key], column.format);
                  if (column.key === "roas") {
                    return (
                      <td
                        key={column.key}
                        className={`${dt.tdCenter} ${getRoasToneClass(row.roas ?? 0)}`}
                      >
                        {value}
                      </td>
                    );
                  }
                  return (
                    <td key={column.key} className={column.center ? dt.tdCenter : dt.number}>
                      {value}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </DashboardDataTableTable>
      </DashboardDataTableScroll>

      <TablePagePagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPrevious={() => setPage(current => Math.max(1, current - 1))}
        onNext={() => setPage(current => Math.min(totalPages, current + 1))}
      />
    </DashboardDataTablePanel>
  );
};

export default PlatformReportTable;
